import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '../api/client.ts';
import { queryDefaults } from '../api/queries.ts';
import { useSession } from '../auth/session.tsx';
import { Link } from 'react-router-dom';
import { LoadFailure } from '../components/LoadFailure.tsx';
import { MoneyBox } from '../components/MoneyBox.tsx';
import { moneyText } from '../moneyInput.ts';
import { dropLabel } from './readingLabels.ts';
import { uncheckedLine } from '../strikeReasonWords.ts';

/** A cost cap as typed ("$1,000" or "1000"), as the number the API takes; NaN when it is not money (F-100). */
const capOf = (s: string) => Number(moneyText(s, { maxDigits: 6 }) ?? NaN);

/**
 * `/admin/reading` — the AI reading run, shadow only (130; Arthur, 19 October 2026). The System Administrator alone.
 * Start a run with a hard cost cap, stop it, watch its progress, and read what it found: each document's type by
 * filename and by the model, and each extracted value with its page and quote, marked found or dropped and why.
 * Nothing here changes a client's record. Results are read only on asking, because each read is audited.
 */
interface Progress {
  id: string | null; state: 'running' | 'stopping' | 'stopped' | 'finished' | null; scope: string | null; model: string | null;
  cost_cap_usd: string | null; cost_spent_usd: string | null; docs_total: number | null; docs_done: number | null; docs_failed: number | null;
  pages_read: number | null; tokens_in: string | null; tokens_out: string | null; started_at: string | null; ended_at: string | null;
  stop_requested: boolean | null; last_error: string | null; type_agrees: number | null; type_differs: number | null;
  /** 135: throttled attempts so far, and the copies left out because an earlier run had read them. */
  docs_retried?: number | string | null; docs_already_read?: number | null;
  /** 174: a strike run's documents checked in part (docs_done counts the complete). */
  docs_in_part?: number | null;
}
interface ReadingState {
  progress: Progress | null;
  /** 174: a strike run's pages not checked, by reason. */
  unchecked?: { reason: string; pages: number }[];
  worker: { step: string; readThisProcess: number; failedThisProcess: number; skippedThisProcess: number; spentThisProcess: number;
            lastProblem: string | null; looping: boolean; throttledThisProcess?: number; concurrency?: number;
            throttles?: { model: number; ocr: number } };
  /** Dev only: the synthetic corpus can be loaded and the latest run scored against its truth files. */
  dev?: boolean;
}
/** One ended run (Arthur, 21 October): numbers and codes only. reread null: not recorded (runs before it was kept). */
interface PastRun {
  id: string; started_at: string; ended_at: string | null; ended_how: 'finished' | 'stopped_by_person' | 'stopped_at_cap' | 'stopped_other';
  scope: 'record' | 'unclassified' | 'both' | 'strike'; reread: boolean | null; docs_total: number; docs_done: number; docs_failed: number;
  docs_already_read: number; pages_read: number; cost_spent_usd: string; cost_cap_usd: string;
  /** 174: a strike run's documents checked in part, beside docs_done (the complete). */
  docs_in_part?: number;
}
interface Estimate { copies: number; unread: number; priced_from_own: number; priced_from_type: number; unpriced: number; all_usd: string; unread_usd: string }
/** 160: what a strike pass would do, from the stored pages, by the worker's own page rule. */
interface StrikeEstimate {
  again: boolean; maxPages: number; strikeCheckOn: boolean; usd: number;
  estimate: { docs: number; already_checked: number; to_check: number; pages_sent: number; pages_held: number; pages_past_limit: number };
}
interface TypeRow { type_by_name: string | null; type_by_model: string | null; docs: number }
interface Field { field: string; value: string | null; page: number | null; quote: string | null; quote_ok: boolean; drop_reason: string | null }
interface Result { doc_id: string; clio_copy_id: string; matter_digits: string | null; type_by_name: string | null; type_by_model: string | null;
  type_agrees: boolean | null; status: string; fields: Field[] | null }

const ENDED: Record<PastRun['ended_how'], string> = {
  finished: 'Finished', stopped_by_person: 'Stopped by a person', stopped_at_cap: 'Stopped at the cost cap', stopped_other: 'Stopped (other)',
};
const SCOPE: Record<PastRun['scope'], string> = { record: 'Record-changing court papers', unclassified: 'Unclassified documents', both: 'Both',
  strike: 'Strike pass (documents already read)' };
/** How a past run chose its documents, in words: a strike pass checks again, a reading reads again. */
const which = (r: PastRun) => (r.reread == null ? 'not recorded'
  : r.scope === 'strike' ? (r.reread ? 'checked everything again' : 'not yet checked only')
  : r.reread ? 'entire library' : 'unread only');
const usd = (v: string | number | null) => (v == null ? '' : `$${Number(v).toFixed(2)}`);
const n = (v: number | string | null) => (v == null ? '0' : Number(v).toLocaleString());
const time = (t: string | null) => (t ? new Date(t).toLocaleString() : '');
const button = 'min-h-[44px] rounded-lg px-4 font-semibold disabled:bg-neutral-200 disabled:text-neutral-600';

export function Reading() {
  const { getToken } = useSession();
  const qc = useQueryClient();
  const [cap, setCap] = useState('');
  const [scope, setScope] = useState<'record' | 'unclassified' | 'both'>('record');
  const [after, setAfter] = useState<string | null>(null);
  // Arthur, 21 October: unread only by default; rereading the entire library shows its estimate and is confirmed once.
  const [reread, setReread] = useState(false);
  const [confirming, setConfirming] = useState(false);

  const q = useQuery({
    queryKey: ['reading'],
    queryFn: () => apiFetch<ReadingState>('/admin/reading', getToken),
    ...queryDefaults,
    refetchInterval: (query) => (['running', 'stopping'].includes(query.state.data?.progress?.state ?? '') ? 10_000 : false),
  });
  const going = ['running', 'stopping'].includes(q.data?.progress?.state ?? '');
  const estimate = useQuery({
    queryKey: ['reading', 'estimate', scope],
    queryFn: () => apiFetch<{ estimate: Estimate | null }>(`/admin/reading/estimate?scope=${scope}`, getToken),
    ...queryDefaults,
    enabled: !!q.data && !going,
  });
  const history = useQuery({
    queryKey: ['reading', 'runs'],
    queryFn: () => apiFetch<{ runs: PastRun[] }>('/admin/reading/runs', getToken),
    ...queryDefaults,
    enabled: !!q.data,
  });
  const types = useMutation({ mutationFn: () => apiFetch<{ types: TypeRow[] }>('/admin/reading/types', getToken) });
  // An audited read: fetched only when its button is pressed, and never refetched by itself.
  const results = useMutation({
    mutationFn: (from: string | null) => apiFetch<{ results: Result[] }>(`/admin/reading/results${from ? `?after=${from}` : ''}`, getToken),
  });
  const start = useMutation({
    mutationFn: () => apiFetch('/admin/reading/start', getToken, { method: 'POST', headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ capUsd: capOf(cap), scope, reread }) }),
    onSuccess: () => { setConfirming(false); setReread(false); void qc.invalidateQueries({ queryKey: ['reading'] }); },
  });
  const aiCheck = useMutation({
    mutationFn: () => apiFetch<{ configured: boolean; di: { status: number | null; error?: string }; openai: { status: number | null; models: string[]; error?: string } }>(
      '/admin/ai/check', getToken),
  });
  const corpusLoad = useMutation({
    mutationFn: () => apiFetch<{ uploaded: number; recorded: number }>('/admin/reading/corpus-load', getToken, { method: 'POST' }),
  });
  const corpusEval = useMutation({ mutationFn: () => apiFetch<Record<string, unknown>>('/admin/reading/corpus-eval', getToken) });
  const stop = useMutation({
    mutationFn: () => apiFetch('/admin/reading/stop', getToken, { method: 'POST' }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['reading'] }),
  });
  // 160, the strike pass: its estimate first, its own cap, and a start that is always confirmed against the estimate.
  const [strikeCap, setStrikeCap] = useState('');
  const [again, setAgain] = useState(false);
  const [strikeConfirming, setStrikeConfirming] = useState(false);
  const strikeEstimate = useQuery({
    queryKey: ['reading', 'strike-estimate', again],
    queryFn: () => apiFetch<StrikeEstimate>(`/admin/reading/strike/estimate?again=${again}`, getToken),
    ...queryDefaults,
    enabled: !!q.data && !going,
  });
  const strikeStart = useMutation({
    mutationFn: () => apiFetch('/admin/reading/strike/start', getToken, { method: 'POST', headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ capUsd: capOf(strikeCap), again }) }),
    onSuccess: () => { setStrikeConfirming(false); setAgain(false); void qc.invalidateQueries({ queryKey: ['reading'] }); },
  });
  const se = strikeEstimate.data;
  const strikeCapOk = capOf(strikeCap) > 0 && Number.isFinite(capOf(strikeCap));
  const d = q.data;
  const e = estimate.data?.estimate ?? null;
  const priceOf = (all: boolean) => (!e ? (estimate.isError ? 'estimate not available' : 'estimating…')
    : `${n(all ? e.copies : e.unread)} documents, about ${usd(all ? e.all_usd : e.unread_usd)}`);
  const p = d?.progress;
  const running = p?.state === 'running' || p?.state === 'stopping';
  const capOk = capOf(cap) > 0 && Number.isFinite(capOf(cap));

  return (
    <div className="min-h-dvh bg-page">
      <div className="mx-auto max-w-5xl px-4 py-6">
        <h1 className="title-bar">Document reading (shadow)</h1>
        <p className="mt-1 text-neutral-700">
          Reads Clio’s copied documents with Document Intelligence and the language model, and keeps what they say here only. Nothing is
          written to any client’s record, and nothing here is shown to staff. Identity numbers are masked before anything is stored or sent.
        </p>
        {q.error && !d ? (
          <LoadFailure what="The reading run" error={q.error} onRetry={() => void q.refetch()} retrying={q.isFetching} forbiddenIsSession={false} />
        ) : !d ? (
          <p className="py-6 text-neutral-600">Loading…</p>
        ) : (
          <>
            <section className="mt-4 rounded-lg border border-line bg-panel p-4">
              <h2 className="text-sm font-bold tracking-[0.08em] text-neutral-600 uppercase">The run</h2>
              {!p?.id ? <p className="mt-1 text-neutral-800">No reading has been run.</p> : (
                <dl className="mt-1 grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-neutral-900">
                  <dt className="text-neutral-700">State</dt>
                  <dd>{p.state === 'running' ? (p.stop_requested ? 'Stopping after the documents in hand' : 'Running') : p.state === 'stopping' ? 'Stopping'
                    : p.state === 'stopped' ? 'Stopped' : 'Finished'}{p.ended_at ? ` · ${time(p.ended_at)}` : ''}{p.last_error ? ` · ${p.last_error}` : ''}</dd>
                  <dt className="text-neutral-700">Scope</dt>
                  <dd>{SCOPE[(p.scope ?? 'both') as PastRun['scope']] ?? p.scope} · {p.model}</dd>
                  <dt className="text-neutral-700">Started</dt><dd>{time(p.started_at)}</dd>
                  <dt className="text-neutral-700">Documents</dt>
                  {p.scope === 'strike' ? (
                    <dd>Of {n(p.docs_total)}: {n(p.docs_done)} checked fully · {n(p.docs_in_part ?? 0)} checked in part · {n(p.docs_failed)} failed
                      {' '}· {n(p.pages_read)} pages sent as pictures
                      {Number(p.docs_already_read ?? 0) > 0 ? ` · ${n(p.docs_already_read ?? 0)} left out, already checked` : ''}
                      {uncheckedLine(d.unchecked ?? []) && <span className="block">{uncheckedLine(d.unchecked ?? [])}</span>}</dd>
                  ) : (
                    <dd>{n(p.docs_done)} of {n(p.docs_total)} read · {n(p.docs_failed)} failed · {n(p.pages_read)} pages
                      {Number(p.docs_already_read ?? 0) > 0 ? ` · ${n(p.docs_already_read ?? 0)} left out, already read by an earlier run` : ''}</dd>
                  )}
                  <dt className="text-neutral-700">Throttling</dt>
                  <dd>{n(p.docs_retried ?? 0)} documents put back to try again · the services answered “too many requests” {n(d.worker.throttles?.model ?? 0)} times
                    (model) and {n(d.worker.throttles?.ocr ?? 0)} (OCR) since this server started · {d.worker.concurrency ?? 4} at a time</dd>
                  <dt className="text-neutral-700">Cost</dt>
                  <dd>{usd(p.cost_spent_usd)} of a {usd(p.cost_cap_usd)} cap (estimated as it goes)</dd>
                  {p.scope !== 'strike' && <><dt className="text-neutral-700">Type</dt>
                    <dd>The model agrees with the filename on {n(p.type_agrees)}, disagrees on {n(p.type_differs)}</dd></>}
                  {running && <><dt className="text-neutral-700">Now</dt>
                    <dd>{d.worker.step} · {d.worker.readThisProcess} read, {d.worker.failedThisProcess} failed since this server started</dd></>}
                </dl>
              )}
              {d.worker.lastProblem && <p role="alert" className="mt-2 text-warning-fg">Last problem: {d.worker.lastProblem}</p>}
              {running ? (
                <button type="button" disabled={!!p?.stop_requested || stop.isPending} onClick={() => stop.mutate()}
                        className={`${button} mt-3 bg-neutral-900 text-white`}>Stop the reading</button>
              ) : (
                // A div, not a form: Enter in the cap must never start a paid run. Only the button starts one.
                <div className="mt-3 flex flex-wrap items-end gap-3">
                  <label className="flex flex-col text-neutral-900">
                    <span className="text-sm text-neutral-700">Cost cap, US dollars (required)</span>
                    <MoneyBox value={cap} onChange={(v) => { setCap(v); setConfirming(false); }} maxDigits={6} required words={false}
                              className="min-h-[44px] rounded-lg border border-line bg-panel px-3" />
                  </label>
                  <label className="flex flex-col text-neutral-900">
                    <span className="text-sm text-neutral-700">What to read</span>
                    <select value={scope} onChange={(ev) => { setScope(ev.target.value as typeof scope); setConfirming(false); }}
                            className="min-h-[44px] rounded-lg border border-line bg-panel px-3">
                      <option value="record">Record-changing court papers</option>
                      <option value="unclassified">Unclassified documents (type only)</option>
                      <option value="both">Both</option>
                    </select>
                  </label>
                  <fieldset className="w-full">
                    <legend className="text-sm text-neutral-700">Which documents</legend>
                    <div className="mt-1 flex flex-wrap gap-2">
                      {([[false, 'Unread only', 'Never read, and those whose last reading failed'],
                         [true, 'Reread entire library', 'Every document in this scope, read before or not']] as const).map(([v, label, what]) => (
                        <label key={label} className={`flex min-h-[44px] cursor-pointer items-start gap-2 rounded-lg border-2 px-3 py-2 text-neutral-900 ${
                          reread === v ? 'border-brand' : 'border-line'} bg-panel`}>
                          <input type="radio" name="reread" checked={reread === v} className="mt-1 h-5 w-5"
                                 onChange={() => { setReread(v); setConfirming(false); }} />
                          <span><span className="font-semibold">{label}</span>
                            <span className="block text-sm text-neutral-700">{what}</span>
                            <span className="block text-sm text-neutral-800">{priceOf(v)}</span></span>
                        </label>
                      ))}
                    </div>
                    {e && e.unpriced > 0 && (
                      <p className="mt-1 text-sm text-neutral-700">{n(e.unpriced)} documents have no earlier reading to price them from and are counted at nothing.</p>
                    )}
                  </fieldset>
                  {confirming ? (
                    <div role="alert" className="w-full rounded-lg border-2 border-line bg-panel p-3 text-neutral-900">
                      <p className="font-semibold">Read the entire library again: {priceOf(true)}?</p>
                      <p className="text-sm text-neutral-700">Every document in this scope is read and paid for again, up to the {usd(cap)} cap.</p>
                      <div className="mt-2 flex flex-wrap gap-3">
                        <button type="button" disabled={start.isPending || !capOk} onClick={() => { if (capOk && !start.isPending) start.mutate(); }}
                                className={`${button} bg-neutral-900 text-white`}>Yes, reread the entire library</button>
                        <button type="button" onClick={() => setConfirming(false)} className={`${button} border border-line bg-panel text-neutral-900`}>Cancel</button>
                      </div>
                    </div>
                  ) : (
                    // A reread only asks here; it starts only from "Yes" above. Unread only starts at once.
                    <button type="button" disabled={start.isPending || !capOk}
                            onClick={() => { if (!capOk || start.isPending) return; if (reread) setConfirming(true); else start.mutate(); }}
                            className={`${button} bg-neutral-900 text-white`}>
                      {reread ? 'Reread entire library…' : 'Start reading'}</button>
                  )}
                </div>
              )}
              {(start.error || stop.error) && <p role="alert" className="mt-2 text-warning-fg">{(start.error ?? stop.error)!.message}</p>}
            </section>

            {!running && (
              <section className="mt-4 rounded-lg border border-line bg-panel p-4">
                <h2 className="text-lg font-semibold">Strike pass</h2>
                <p className="mt-1 text-neutral-800">Checks the orders and commissions already read for words with a line drawn through them. Nothing is read
                  again: each document’s stored pages are looked at as pictures, at most {se?.maxPages ?? 12} a document, never a page that held an
                  identity number. What it finds is added to each document’s review.</p>
                {strikeEstimate.error ? <p role="alert" className="mt-2 text-warning-fg">{strikeEstimate.error.message}</p>
                  : !se ? <p className="mt-2 text-neutral-600">Estimating…</p> : (
                  <>
                    <p className="mt-2 text-neutral-900">{n(se.estimate.to_check)} of {n(se.estimate.docs)} documents to check
                      ({n(se.estimate.already_checked)} already checked) · {n(se.estimate.pages_sent)} candidate pages by their text, at most about {usd(se.usd)}
                      {' '}(a page that cannot be drawn as a picture, or comes out blank, is listed after the pass as not checked)
                      {' '}· {n(se.estimate.pages_held)} pages held an identity number and are not sent · {n(se.estimate.pages_past_limit)} past the page limit</p>
                    {!se.strikeCheckOn && (
                      <p className="mt-2 rounded-lg border border-warning-edge bg-warning-bg p-3 text-warning-fg">
                        The strike check is off on this server, so a pass cannot start. It is switched on by the READING_STRIKE_CHECK setting.</p>
                    )}
                  </>
                )}
                {/* A div, not a form: Enter in the cap must never start a paid pass. */}
                <div className="mt-3 flex flex-wrap items-end gap-3">
                  <label className="flex flex-col text-neutral-900">
                    <span className="text-sm text-neutral-700">Cost cap, US dollars (required)</span>
                    <MoneyBox value={strikeCap} onChange={(v) => { setStrikeCap(v); setStrikeConfirming(false); }} maxDigits={6} required
                              words={false} className="min-h-[44px] rounded-lg border border-line bg-panel px-3" />
                  </label>
                  <fieldset>
                    <legend className="text-sm text-neutral-700">Which documents</legend>
                    <div className="mt-1 flex flex-wrap gap-2">
                      {([[false, 'Not yet checked'], [true, 'Check everything again']] as const).map(([v, label]) => (
                        <label key={label} className={`flex min-h-[44px] cursor-pointer items-center gap-2 rounded-lg border-2 px-3 py-2 text-neutral-900 ${
                          again === v ? 'border-brand' : 'border-line'} bg-panel`}>
                          <input type="radio" name="strike-again" checked={again === v} className="h-5 w-5"
                                 onChange={() => { setAgain(v); setStrikeConfirming(false); }} />
                          <span className="font-semibold">{label}</span>
                        </label>
                      ))}
                    </div>
                  </fieldset>
                  {strikeConfirming && se ? (
                    <div role="alert" className="w-full rounded-lg border-2 border-line bg-panel p-3 text-neutral-900">
                      <p className="font-semibold">Start the strike pass: {n(se.estimate.to_check)} documents, {n(se.estimate.pages_sent)} pages,
                        about {usd(se.usd)}?</p>
                      <p className="text-sm text-neutral-700">It stops at the {usd(strikeCap)} cap whatever the estimate says.</p>
                      <div className="mt-2 flex flex-wrap gap-3">
                        <button type="button" disabled={strikeStart.isPending || !strikeCapOk}
                                onClick={() => { if (strikeCapOk && !strikeStart.isPending) strikeStart.mutate(); }}
                                className={`${button} bg-neutral-900 text-white`}>Yes, start the strike pass</button>
                        <button type="button" onClick={() => setStrikeConfirming(false)} className={`${button} border border-line bg-panel text-neutral-900`}>Cancel</button>
                      </div>
                    </div>
                  ) : (
                    <button type="button" disabled={!strikeCapOk || !se?.strikeCheckOn || strikeStart.isPending}
                            onClick={() => { if (strikeCapOk && se?.strikeCheckOn) setStrikeConfirming(true); }}
                            className={`${button} bg-neutral-900 text-white`}>Start a strike pass…</button>
                  )}
                </div>
                {strikeStart.error && <p role="alert" className="mt-2 text-warning-fg">{strikeStart.error.message}</p>}
              </section>
            )}

            <section className="mt-4">
              <h2 className="text-lg font-semibold">Past runs</h2>
              {history.error ? <p role="alert" className="mt-1 text-warning-fg">{history.error.message}</p>
                : !history.data ? <p className="mt-1 text-neutral-600">Loading…</p>
                : history.data.runs.length === 0 ? <p className="mt-1 text-neutral-800">No run has ended yet.</p> : (
                <div className="mt-2 overflow-x-auto">
                  <table className="w-full text-left text-sm text-neutral-900">
                    <thead><tr className="text-neutral-700">
                      <th className="py-1 pr-3">Started</th><th className="pr-3">Ended</th><th className="pr-3">How it ended</th><th className="pr-3">Scope</th>
                      <th className="pr-3 text-right">Done</th><th className="pr-3 text-right">Failed</th><th className="pr-3 text-right">Pages</th><th className="text-right">Cost</th>
                    </tr></thead>
                    <tbody>{history.data.runs.map((r) => (
                      <tr key={r.id} className="border-t border-line align-top">
                        <td className="py-1 pr-3">{time(r.started_at)}</td><td className="pr-3">{time(r.ended_at)}</td>
                        <td className="pr-3">{ENDED[r.ended_how] ?? r.ended_how}</td>
                        <td className="pr-3">{SCOPE[r.scope] ?? r.scope} · {which(r)}</td>
                        <td className="pr-3 text-right">{n(r.docs_done)} of {n(r.docs_total)}{Number(r.docs_in_part ?? 0) > 0 ? ` (+${n(r.docs_in_part ?? 0)} in part)` : ''}</td><td className="pr-3 text-right">{n(r.docs_failed)}</td>
                        <td className="pr-3 text-right">{n(r.pages_read)}</td><td className="text-right">{usd(r.cost_spent_usd)}</td>
                      </tr>))}</tbody>
                  </table>
                </div>
              )}
            </section>

            <section className="mt-4">
              <h2 className="text-lg font-semibold">The AI services, from inside the app</h2>
              <p className="mt-1 text-neutral-800">Reaches Document Intelligence and the OpenAI account with the app's own identity, through the private endpoints. Status codes and model names only.</p>
              <div className="mt-2 flex flex-wrap gap-3">
                <button type="button" disabled={aiCheck.isPending} onClick={() => aiCheck.mutate()}
                  className={`${button} border border-line bg-panel text-neutral-900`}>AI check</button>
              </div>
              {aiCheck.error && <p role="alert" className="mt-2 text-warning-fg">{aiCheck.error.message}</p>}
              {aiCheck.data && (
                <dl className="mt-2 grid grid-cols-[auto_1fr] gap-x-4 gap-y-1">
                  <dt className="text-neutral-700">Configured</dt><dd>{aiCheck.data.configured ? 'yes' : 'no: the AI_ settings are missing'}</dd>
                  <dt className="text-neutral-700">Document Intelligence</dt>
                  <dd>{aiCheck.data.di.status ?? 'no answer'}{aiCheck.data.di.status === 200 ? ' (reached)' : ''}{aiCheck.data.di.error ? ` · ${aiCheck.data.di.error}` : ''}</dd>
                  <dt className="text-neutral-700">OpenAI</dt>
                  <dd>{aiCheck.data.openai.status ?? 'no answer'}{aiCheck.data.openai.status === 200 ? ' (reached)' : ''}{aiCheck.data.openai.error ? ` · ${aiCheck.data.openai.error}` : ''}</dd>
                  <dt className="text-neutral-700">Models</dt><dd>{aiCheck.data.openai.models.length ? aiCheck.data.openai.models.join(', ') : 'none listed'}</dd>
                </dl>
              )}
            </section>

            {q.data?.dev && (
              <section className="mt-4">
                <h2 className="text-lg font-semibold">The synthetic corpus (dev only)</h2>
                <div className="mt-2 flex flex-wrap gap-3">
                  <button type="button" disabled={corpusLoad.isPending} onClick={() => corpusLoad.mutate()}
                    className={`${button} border border-line bg-panel text-neutral-900`}>Load the corpus</button>
                  <button type="button" disabled={corpusEval.isPending} onClick={() => corpusEval.mutate()}
                    className={`${button} border border-line bg-panel text-neutral-900`}>Score the latest run against the corpus</button>
                </div>
                {corpusLoad.data && <p className="mt-2">Listed {corpusLoad.data.recorded} new; stored and verified {corpusLoad.data.uploaded}.</p>}
                {(corpusLoad.error || corpusEval.error) && <p role="alert" className="mt-2 text-warning-fg">{(corpusLoad.error ?? corpusEval.error)!.message}</p>}
                {corpusEval.data && <pre className="mt-2 max-h-[32rem] overflow-auto rounded border border-line bg-panel p-2 text-sm text-neutral-900">{JSON.stringify(corpusEval.data, null, 1)}</pre>}
              </section>
            )}

            <section className="mt-4">
              <div className="flex flex-wrap gap-3">
                <button type="button" onClick={() => types.mutate()} className={`${button} border border-line bg-panel text-neutral-900`}>Types: filename against model</button>
                <button type="button" onClick={() => { setAfter(null); results.mutate(null); }} className={`${button} border border-line bg-panel text-neutral-900`}>
                  Read the results</button>
              </div>
              <p className="mt-1 text-sm text-neutral-700">Reading the results is recorded as a look: they hold what the documents say.</p>
              {types.error && <p role="alert" className="mt-2 text-warning-fg">{types.error.message}</p>}
              {types.data && (
                <div className="mt-2 overflow-x-auto">
                  <table className="w-full text-left text-neutral-900">
                    <thead><tr className="text-sm text-neutral-700"><th className="py-1 pr-3">By filename</th><th className="pr-3">By the model</th><th>Documents</th></tr></thead>
                    <tbody>{types.data.types.map((t) => (
                      <tr key={`${t.type_by_name}|${t.type_by_model}`} className="border-t border-line">
                        <td className="py-1 pr-3">{t.type_by_name ?? 'Unclassified'}</td><td className="pr-3">{t.type_by_model ?? 'Unknown'}</td><td>{n(t.docs)}</td>
                      </tr>))}</tbody>
                  </table>
                </div>
              )}
              {results.error && <p role="alert" className="mt-2 text-warning-fg">{results.error.message}</p>}
              {results.data && (
                results.data.results.length === 0 ? <p className="mt-2 text-neutral-800">No results{after ? ' after these' : ''}.</p> : (
                  <div className="mt-2 space-y-3">
                    {results.data.results.map((r) => (
                      <article key={r.doc_id} className="rounded-lg border border-line bg-panel p-3 text-neutral-900">
                        <h3 className="font-semibold">Matter {r.matter_digits ?? '(none)'} · {r.type_by_name ?? 'Unclassified'}
                          {r.type_by_model && r.type_by_model !== r.type_by_name ? ` · the model says ${r.type_by_model}` : ''} · {r.status}</h3>
                        <Link to={`/admin/reading/docs/${r.doc_id}`} className="mt-1 inline-flex min-h-[44px] items-center font-semibold text-link underline">
                          Review beside the PDF</Link>
                        {r.fields && r.fields.length > 0 && (
                          <div className="mt-1 overflow-x-auto">
                            <table className="w-full text-left text-sm">
                              <thead><tr className="text-neutral-700"><th className="py-1 pr-3">Field</th><th className="pr-3">Value</th><th className="pr-3">Page</th>
                                <th className="pr-3">Quote</th><th>Checked</th></tr></thead>
                              <tbody>{r.fields.map((f, i) => (
                                <tr key={`${f.field}-${i}`} className="border-t border-line align-top">
                                  <td className="py-1 pr-3">{f.field}</td><td className="pr-3">{f.value ?? ''}</td><td className="pr-3">{f.page ?? ''}</td>
                                  <td className="pr-3">{f.quote ?? ''}</td>
                                  <td>{f.quote_ok ? 'Found' : `Dropped: ${dropLabel(f.drop_reason)}`}</td>
                                </tr>))}</tbody>
                            </table>
                          </div>
                        )}
                      </article>
                    ))}
                    <button type="button" onClick={() => { const last = results.data!.results.at(-1)!.doc_id; setAfter(last); results.mutate(last); }}
                            className={`${button} border border-line bg-panel text-neutral-900`}>Next 50</button>
                  </div>
                )
              )}
            </section>
          </>
        )}
      </div>
    </div>
  );
}
